/**
 * PostHog Analytics — Frontend Helper
 *
 * Setup:
 *   1. Add REACT_APP_POSTHOG_KEY=phc_... to frontend/.env
 *   2. Add REACT_APP_POSTHOG_HOST=https://eu.i.posthog.com (or us.i.posthog.com) to frontend/.env
 *   3. Events fire automatically — no further config needed
 *
 * If keys are missing, all calls are silent no-ops (safe for dev).
 */

import posthog from "posthog-js";

// ── Event Names (constants) ────────────────────────────
export const EVENTS = {
  LANDING_VIEWED: "landing_viewed",
  ADDRESS_INPUT_STARTED: "address_input_started",
  ADDRESS_CHECKED: "address_checked",
  ADDRESS_AVAILABLE: "address_available",
  CREATE_CLICKED: "create_clicked",
  ALIAS_CREATED: "alias_created",
};

// ── Init ───────────────────────────────────────────────
let initialized = false;

export function initPostHog() {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";

  if (!key) {
    console.debug("[Analytics] REACT_APP_POSTHOG_KEY not set — tracking disabled");
    return;
  }

  posthog.init(key, {
    api_host: host,
    capture_pageview: false, // we track manually
    capture_pageleave: true,
    persistence: "localStorage+cookie",
    autocapture: false, // we define our own events
  });
  initialized = true;
}

// ── Helpers ────────────────────────────────────────────
function isActive() {
  return initialized && typeof posthog.capture === "function";
}

export function getDistinctId() {
  return isActive() ? posthog.get_distinct_id() : null;
}

export function getSessionId() {
  if (!isActive()) return null;
  const props = posthog.get_session_id?.() || null;
  return props;
}

// ── A/B Variant ────────────────────────────────────────
// Stable per session — stored in sessionStorage
const VARIANT_KEY = "ab_hero_variant";

export function getVariant() {
  let v = sessionStorage.getItem(VARIANT_KEY);
  if (!v) {
    v = Math.random() < 0.5 ? "A" : "B";
    sessionStorage.setItem(VARIANT_KEY, v);
  }
  return v;
}

// ── Core Track Function ────────────────────────────────
export function track(eventName, properties = {}) {
  const variant = getVariant();
  const payload = { variant, ...properties };

  if (isActive()) {
    posthog.capture(eventName, payload);
  }

  // Always log in dev for debugging
  if (process.env.NODE_ENV === "development") {
    console.debug(`[Analytics] ${eventName}`, payload);
  }
}

// ── Convenience Functions ──────────────────────────────

let landingTimestamp = null;

export function trackLandingViewed(extraProps = {}) {
  landingTimestamp = Date.now();
  track(EVENTS.LANDING_VIEWED, {
    referrer: document.referrer || "direct",
    device_type: window.innerWidth < 768 ? "mobile" : "desktop",
    ...extraProps,
  });
}

export function trackAddressInputStarted() {
  track(EVENTS.ADDRESS_INPUT_STARTED);
}

export function trackAddressChecked(inputLength, result, responseTimeMs) {
  track(EVENTS.ADDRESS_CHECKED, {
    input_length: inputLength,
    result, // "available" | "taken" | "not_allowed" | "invalid"
    response_time_ms: responseTimeMs,
  });
}

export function trackAddressAvailable(addressLength) {
  track(EVENTS.ADDRESS_AVAILABLE, {
    address_length: addressLength,
    has_dots_or_dashes: /[._-]/.test(sessionStorage.getItem("last_address_input") || ""),
  });
}

export function trackCreateClicked(addressLength, attemptsBefore = 0) {
  const timeSinceLanding = landingTimestamp ? Date.now() - landingTimestamp : null;
  track(EVENTS.CREATE_CLICKED, {
    address_length: addressLength,
    time_since_landing_ms: timeSinceLanding,
    attempts_before_success: attemptsBefore,
  });
}

export function trackAliasCreated(addressLength) {
  const timeSinceLanding = landingTimestamp ? Date.now() - landingTimestamp : null;
  track(EVENTS.ALIAS_CREATED, {
    address_length: addressLength,
    time_since_landing_ms: timeSinceLanding,
  });
}
